import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';

export default function Footer() {
  const [signedIn, setSignedIn] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSignedIn(!!data.session);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(!!session);
    });
    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  return (
    <footer className="w-full bg-primary-500 text-white">
      {/* Main Footer */}
      <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-8">
          {/* Logo & Social - 1 col */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <span className="font-heading text-2xl font-bold">Vietnam</span>
              <span className="text-xs font-medium tracking-widest uppercase block">story</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <a href="#" className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                <i className="ri-instagram-line text-sm" />
              </a>
              <a href="#" className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                <i className="ri-facebook-fill text-sm" />
              </a>
              <a href="#" className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                <i className="ri-youtube-fill text-sm" />
              </a>
              <a href="#" className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                <i className="ri-tiktok-fill text-sm" />
              </a>
            </div>
          </div>

          {/* Licensed & Verified - Replaces About Meetup + Travel, spans 2 cols */}
          <div className="lg:col-span-2 border border-white/40 rounded-xl p-5 md:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-lg" />
              </div>
              <div>
                <p className="text-xs font-bold tracking-widest uppercase text-white/80">{t('footer.licensedVerified')}</p>
                <p className="text-sm font-semibold">{t('footer.officialTourOperator')}</p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-2.5">
                <i className="ri-building-line text-white/60 text-sm mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] font-medium text-white/60 uppercase tracking-wider">{t('footer.company')}</p>
                  <p className="text-sm font-medium leading-snug">RED LION INTERNATIONAL TRAVEL COMPANY LIMITED</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <i className="ri-barcode-line text-white/60 text-sm mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] font-medium text-white/60 uppercase tracking-wider">{t('footer.licenseNo')}</p>
                  <p className="text-sm font-medium">01-2641/2024/CDLQGVN-GP LHQT</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <i className="ri-map-pin-line text-white/60 text-sm mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] font-medium text-white/60 uppercase tracking-wider">{t('footer.headquarters')}</p>
                  <p className="text-sm font-medium leading-snug">Số 10/2, Ngách 29, Ngõ 264, Tổ 13, Phường Bồ Đề, Hà Nội</p>
                </div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-white/20 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1 bg-white/20 text-white px-2.5 py-1 rounded-full text-[11px] font-semibold">
                <i className="ri-checkbox-circle-line text-xs" />
                {t('footer.verified')}
              </span>
              <span className="inline-flex items-center gap-1 bg-white/20 text-white px-2.5 py-1 rounded-full text-[11px] font-semibold">
                <i className="ri-award-line text-xs" />
                {t('footer.licensed')}
              </span>
              <span className="inline-flex items-center gap-1 bg-white/20 text-white px-2.5 py-1 rounded-full text-[11px] font-semibold">
                <i className="ri-earth-line text-xs" />
                {t('footer.intlOperator')}
              </span>
            </div>
          </div>

          {/* Contact - 2 cols */}
          <div className="lg:col-span-2">
            <h3 className="font-medium text-sm uppercase tracking-wider mb-4">{t('footer.contact')}</h3>
            <ul className="space-y-2.5 text-sm text-white/80">
              <li className="flex items-start gap-2">
                <i className="ri-phone-line mt-0.5 shrink-0" />
                <span>+84 362923218</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-mail-line mt-0.5 shrink-0" />
                <span>admin@vietnam.st</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-map-pin-line mt-0.5 shrink-0" />
                <span>53 Ly Nam De, Ha noi</span>
              </li>
            </ul>

            {/* Payment Methods */}
            <div className="mt-6">
              <h3 className="font-medium text-sm uppercase tracking-wider mb-4">{t('footer.paymentMethods')}</h3>
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  <i className="ri-visa-fill text-base" />VISA
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  <i className="ri-mastercard-fill text-base" />Mastercard
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  JCB
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  <i className="ri-paypal-fill text-base" />PayPal
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  <i className="ri-bit-coin-fill text-base" />Crypto
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  AMEX
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  <i className="ri-google-fill text-base" />Pay
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/20 text-white rounded-md px-3 py-2 text-xs font-bold whitespace-nowrap">
                  <i className="ri-apple-fill text-base" />Pay
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="w-full px-4 md:px-8 lg:px-12 py-4 border-t border-white/20">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/60 text-center">
            {t('footer.copyright')}
          </p>
          <Link
            to={signedIn ? '/profile' : '/login'}
            className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-white/20 hover:bg-white/30 rounded-md px-4 py-2 transition-colors whitespace-nowrap"
          >
            <i className={signedIn ? 'ri-user-3-line' : 'ri-user-line'} />
            {signedIn ? t('nav.myAccount') : t('nav.signIn')}
          </Link>
        </div>
      </div>
    </footer>
  );
}